import { PositionSetCreateCommand } from '@view-model/models/common/PositionSet/PositionSetCreateCommand';
import { ModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ModelCascadeRepository';
import { ModelCreateCommand } from '@view-model/command/model/ModelCreateCommand';
import { ConsistencyLink } from '@view-model/domain/ConsistencyLink';
import type { CommandManager } from '@model-framework/command';
import { CompositeCommand, CreateCommand } from '@model-framework/command';
import { ViewModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ViewModelCascadeRepository';
import { ViewModelClipboardPayload } from '@view-model/application/operation/ViewModelClipboardPayload';
import { ObjectRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import type { ViewModelContent } from '@view-model/domain/view-model/ViewModelContent';
import { ViewModelAssetCollectionForClone } from '@view-model/domain/view-model';
import { FileContainerAssetCloner } from '@view-model/domain/view-model/FileContainerAssetCloner';
import { FileContainerAssetTrashCommand } from '@view-model/models/sticky/FileContainer/command/FileContainerAssetTrashCommand';
import type { GroupId, UserId, ViewId, ViewModelId, WorkspaceId } from 'balus-common';
import { ClipboardUtils } from '@framework/ClipboardUtils';
import { t } from 'i18next';

type PasteWithoutCommandResult =
  { ok: true; errorMessage: string | null; viewIdMap: Record<string, string> } | { ok: false; errorMessage: string };

export class ViewModelClipboardOperator {
  public static async copy(workspaceId: WorkspaceId, viewModelId: ViewModelId): Promise<boolean> {
    const repository = new ViewModelCascadeRepository(viewModelId);

    const content = await repository.loadContent().catch((e: unknown) => console.error(e));
    if (!content) return false;

    const payload = ViewModelClipboardPayload.fromContent(content, workspaceId, viewModelId);
    try {
      await ClipboardUtils.copyWithCustomFormat({ json: JSON.stringify(payload) });
      return true;
    } catch (e) {
      console.warn('クリップボードへのコピーに失敗しました', e);
      return false;
    }
  }

  private static async parsePayloadForPaste(
    groupId: GroupId,
    viewModelId: ViewModelId,
    currentUserId: UserId,
    payload: ViewModelClipboardPayload
  ): Promise<
    | { ok: true; viewModelContent: ViewModelContent; errorMessage: string | null; viewIdMap: Record<string, string> }
    | { ok: false; errorMessage: string }
  > {
    // ペイロードから復元
    const originalContent = ViewModelClipboardPayload.toContent(payload);

    const fileAssetClone = await FileContainerAssetCloner.clone(
      payload.sourceViewModelId,
      { groupId, viewModelId },
      originalContent.getModels().fileContainerAssetIds(),
      currentUserId
    );
    if (!fileAssetClone.ok) return fileAssetClone;

    // 復元したペイロード中に含まれる ViewModelAssets の情報を取得して複製する
    const assets = await ViewModelAssetCollectionForClone.fetchByUrlList(originalContent.assetUrls());
    const baseUrl = location.href;
    const [assetUrlMap, hasError] = await assets.cloneNew(groupId, viewModelId, currentUserId, baseUrl);
    const errorMessage = hasError ? t('canvas.asset.cloneFailed') : null;

    // 識別子を置き換えながら複製
    const [viewModelContent, viewIdMap] = originalContent.cloneNewWithViewIdMap(assetUrlMap, fileAssetClone.assetCopy);
    // 同じビューモデル内でのコピペであれば、表示位置を調整する
    if (payload.sourceViewModelId === viewModelId) {
      viewModelContent.moveViewPositions(600, 300);
    }
    viewModelContent.correctViewOrders(0);

    return { ok: true, viewModelContent, errorMessage, viewIdMap };
  }

  /**
   * コマンド履歴に登録する形でビューモデルの貼り付け操作を行う。
   * @param groupId 貼り付け対象のビューモデルが属するグループID
   * @param viewModelKey 貼り付け対象のビューモデル・キー
   * @param currentUserId 貼り付け操作を行なっているユーザID
   * @param payload ビューモデル全体を表現するクリップボードペイロード
   * @param commandManager コマンドマネージャ
   * @returns 作成したビュー・キーの一覧(配列)と、エラーがある場合にはエラー文言
   */
  public static async pasteWithCommand(
    groupId: GroupId,
    viewModelId: ViewModelId,
    currentUserId: UserId,
    payload: ViewModelClipboardPayload,
    commandManager: CommandManager
  ): Promise<[Set<ViewId>, string | null]> {
    const parsed = await this.parsePayloadForPaste(groupId, viewModelId, currentUserId, payload);
    if (!parsed.ok) return [new Set(), parsed.errorMessage];
    const { viewModelContent, errorMessage } = parsed;

    const viewCreateCommands = viewModelContent.getViews().map((view) => {
      const repository = new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(viewModelId, view.id));
      return new CreateCommand(view, repository);
    });
    const viewPositionCreateCommand = new PositionSetCreateCommand(
      viewModelContent.viewPositions,
      RTDBPath.View.positionsPath(viewModelId)
    );

    const modelCascadingRepository = new ModelCascadeRepository(viewModelId);
    const modelCreateCommands = viewModelContent
      .getModels()
      .list()
      .map((modelContents) => {
        return new ModelCreateCommand(modelCascadingRepository, modelContents);
      });

    const consistencyLinkCommands = viewModelContent.consistencyLinks.map((consistencyLink) => {
      const repository = new ObjectRepository(
        ConsistencyLink,
        RTDBPath.ConsistencyLink.linkPath(viewModelId, consistencyLink.id)
      );
      return new CreateCommand(consistencyLink, repository);
    });

    const command = new CompositeCommand(
      ...viewCreateCommands,
      viewPositionCreateCommand,
      ...modelCreateCommands,
      ...consistencyLinkCommands
    );
    FileContainerAssetTrashCommand.executeCreation(
      commandManager,
      { groupId, viewModelId },
      viewModelContent.getModels().fileContainerAssetIds(),
      command
    );

    return [new Set(viewModelContent.getViews().map((view) => view.id)), errorMessage];
  }

  /**
   * コマンド履歴に登録しない形でビューモデル全体のクリップボードペイロードを貼り付けする。
   * @param groupId 貼り付け対象のビューモデルが属するグループID
   * @param viewModelKey 貼り付け対象のビューモデル・キー
   * @param currentUserId 貼り付け操作を行なっているユーザID
   * @param payload ビューモデル全体を表現するクリップボードペイロード
   * @returns エラーのある場合にはエラー文言と、複製前後のviewId対応を返す。ファイルを複製できなかった場合は何も保存せず ok: false を返す
   */
  public static async pasteWithoutCommand(
    groupId: GroupId,
    viewModelId: ViewModelId,
    currentUserId: UserId,
    payload: ViewModelClipboardPayload
  ): Promise<PasteWithoutCommandResult> {
    const parsed = await this.parsePayloadForPaste(groupId, viewModelId, currentUserId, payload);
    if (!parsed.ok) return parsed;
    const { viewModelContent, errorMessage, viewIdMap } = parsed;
    const viewModelCascadeRepository = new ViewModelCascadeRepository(viewModelId);

    // 保存（具現化）
    await viewModelCascadeRepository.saveContent(viewModelContent);

    return { ok: true, errorMessage, viewIdMap };
  }
}
